import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Upload, FileText, Download, Loader2, AlertCircle, CheckCircle2, Mail } from 'lucide-react';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';
import { meetingMinutesApi } from '../services/api';
import AppHeader from '../components/common/AppHeader';
import Toast from '../components/ui/Toast';

const MEETING_TYPES = [
  { value: 'interview_rv1', label: 'Entretien RV1 — Pre-qualification RH' },
  { value: 'interview_rv2', label: 'Entretien RV2 — Qualification technique' },
  { value: 'generic', label: 'Reunion generique' },
  { value: 'commercial', label: 'Reunion commerciale hebdo' },
];

const ACCEPTED_FORMATS = '.mp4,.m4a,.wav,.mp3,.webm,.mkv,.avi,.vtt,.docx';
const VIDEO_EXTENSIONS = new Set(['.mp4', '.mkv', '.webm', '.avi']);
const TRANSCRIPT_EXTENSIONS = new Set(['.vtt', '.docx']);
const MAX_FILE_SIZE_MB = 500;
const POLL_INTERVAL_MS = 5000;

const STATUS_LABELS = {
  pending: 'En attente...',
  processing: 'Traitement en cours...',
  extracting: 'Extraction audio en cours...',
  transcribing: 'Transcription audio...',
  diarizing: 'Identification des locuteurs...',
  generating: 'Generation du compte-rendu...',
  completed: 'Termine',
  failed: 'Echec',
};

const extractAudioFromVideo = async (videoFile, onProgress) => {
  const ffmpeg = new FFmpeg();
  ffmpeg.on('progress', ({ progress }) => {
    onProgress?.(Math.round(progress * 100));
  });

  try {
    await ffmpeg.load();
    const dotIndex = videoFile.name.lastIndexOf('.');
    const inputName = 'input' + (dotIndex !== -1 ? videoFile.name.substring(dotIndex) : '.mp4');
    await ffmpeg.writeFile(inputName, await fetchFile(videoFile));
    await ffmpeg.exec(['-i', inputName, '-vn', '-ar', '16000', '-ac', '1', '-b:a', '64k', 'output.mp3']);
    const data = await ffmpeg.readFile('output.mp3');

    if (!data || data.byteLength === 0) {
      throw new Error('L\'extraction audio n\'a produit aucun resultat.');
    }

    const audioName = videoFile.name.replace(/\.[^.]+$/, '.mp3');
    return new File([data], audioName, { type: 'audio/mpeg' });
  } catch (err) {
    if (err.message?.includes('SharedArrayBuffer')) {
      throw new Error('Votre navigateur ne supporte pas l\'extraction video. Utilisez un fichier audio.');
    }
    throw err;
  } finally {
    ffmpeg.terminate();
  }
};

const MeetingMinutes = () => {
  const [meetingType, setMeetingType] = useState('interview_rv1');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [extractionProgress, setExtractionProgress] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingDocx, setDownloadingDocx] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sourceType, setSourceType] = useState(null);
  const pollRef = useRef(null);
  const fileInputRef = useRef(null);

  const clearPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Poll job status
  useEffect(() => {
    if (!jobId) return;

    const poll = async () => {
      try {
        const status = await meetingMinutesApi.getStatus(jobId);
        setJobStatus(status.status);

        if (status.status === 'completed' || status.status === 'failed') {
          clearPolling();
          if (status.status === 'failed') {
            setError(status.error || 'La generation a echoue.');
          }
        }
      } catch (err) {
        clearPolling();
        setError('Erreur lors de la verification du statut.');
      }
    };

    poll();
    pollRef.current = setInterval(poll, POLL_INTERVAL_MS);

    return clearPolling;
  }, [jobId, clearPolling]);

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    if (selected.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setToast({ message: `Le fichier depasse la limite de ${MAX_FILE_SIZE_MB} Mo.`, type: 'error' });
      return;
    }

    setFile(selected);
    setError(null);
    setJobId(null);
    setJobStatus(null);
  };

  const handleGenerate = async () => {
    if (!file) return;

    setUploading(true);
    setError(null);
    setJobId(null);
    setJobStatus(null);

    let wakeLock = null;
    try {
      if (navigator.wakeLock) {
        wakeLock = await navigator.wakeLock.request('screen');
      }
      const fileExt = '.' + file.name.split('.').pop().toLowerCase();
      let objectKey;
      let uploadFilename = file.name;
      const isTranscript = TRANSCRIPT_EXTENSIONS.has(fileExt);

      const isVideo = VIDEO_EXTENSIONS.has(fileExt);

      if (isTranscript) {
        // Transcript file: upload to backend for parsing
        const result = await meetingMinutesApi.uploadTranscript(file);
        objectKey = result.objectKey;
        uploadFilename = result.filename;
      } else if (isVideo) {
        // Video file: extract audio client-side, send to backend for Whisper transcription
        setExtractionProgress(0);
        const audioFile = await extractAudioFromVideo(file, setExtractionProgress);
        setExtractionProgress(null);
        uploadFilename = audioFile.name;
        const result = await meetingMinutesApi.uploadVideo(audioFile);
        objectKey = result.objectKey;
      } else {
        // Audio file: direct S3 upload via presigned URL
        const { uploadUrl, objectKey: key } = await meetingMinutesApi.upload(file.name);
        objectKey = key;

        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
        });

        if (!uploadResponse.ok) {
          throw new Error('Echec de l\'upload du fichier.');
        }
      }

      // Transcript files (VTT/DOCX) are pre-parsed; audio and video go through worker pipeline
      const effectiveSourceType = isTranscript ? 'transcript' : 'audio';
      const { jobId: newJobId } = await meetingMinutesApi.generate(
        objectKey, meetingType, uploadFilename, effectiveSourceType
      );
      setJobId(newJobId);
      setSourceType(effectiveSourceType);
      setToast({ message: 'Generation lancee avec succes.', type: 'success' });
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'Erreur lors de la generation.';
      setError(message);
      setToast({ message, type: 'error' });
    } finally {
      if (wakeLock) wakeLock.release();
      setUploading(false);
      setExtractionProgress(null);
    }
  };

  const handleDownload = async () => {
    if (!jobId) return;

    setDownloading(true);
    try {
      const { downloadUrl } = await meetingMinutesApi.getResult(jobId);
      window.open(downloadUrl, '_blank');
      setToast({ message: 'Telechargement Markdown lance.', type: 'success' });
    } catch (err) {
      console.error('[handleDownload] failed:', err);
      const message = err?.response?.data?.detail || err?.message || 'Erreur lors du telechargement.';
      setToast({ message, type: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadDocx = async () => {
    if (!jobId) return;

    setDownloadingDocx(true);
    try {
      const blob = await meetingMinutesApi.getDocx(jobId);
      const url = URL.createObjectURL(
        new Blob([blob], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' })
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = 'compte-rendu.docx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setToast({ message: 'Telechargement Word lance.', type: 'success' });
    } catch (err) {
      console.error('[handleDownloadDocx] failed:', err);
      const message = err?.response?.data?.detail || err?.message || 'Erreur lors du telechargement DOCX.';
      setToast({ message, type: 'error' });
    } finally {
      setDownloadingDocx(false);
    }
  };

  const handleEmail = async () => {
    if (!jobId) return;

    setSendingEmail(true);
    try {
      const { markdown } = await meetingMinutesApi.getContent(jobId);

      if (!markdown) {
        setToast({ message: 'Le contenu du compte-rendu est vide.', type: 'error' });
        return;
      }

      const subject = encodeURIComponent('Compte-rendu de reunion');
      // mailto body has a ~2000 char practical limit; truncate if needed
      const maxLen = 1800;
      const wasTruncated = markdown.length > maxLen;
      const truncated = wasTruncated
        ? markdown.slice(0, maxLen) + '\n\n[... tronque, voir piece jointe]'
        : markdown;
      const body = encodeURIComponent(truncated);
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
      setToast({
        message: wasTruncated
          ? 'Email prepare (contenu tronque — voir piece jointe pour la version complete).'
          : 'Email prepare, votre client mail va s\'ouvrir.',
        type: 'success',
      });
    } catch (err) {
      console.error('[handleEmail] failed:', err);
      const message = err?.response?.data?.detail || err?.message || 'Erreur lors de la preparation de l\'email.';
      setToast({ message, type: 'error' });
    } finally {
      setSendingEmail(false);
    }
  };

  const handleReset = () => {
    clearPolling();
    setFile(null);
    setJobId(null);
    setJobStatus(null);
    setSourceType(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const isProcessing = jobId && jobStatus && !['completed', 'failed'].includes(jobStatus);
  const isCompleted = jobStatus === 'completed';
  const isFailed = jobStatus === 'failed';

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader showSearch={false} />

      <main className="p-6 max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Comptes-rendus de reunions</h1>

        {/* Meeting type selector */}
        <div className="mb-6">
          <label htmlFor="meeting-type" className="block text-sm font-medium text-gray-700 mb-2">
            Type de reunion
          </label>
          <select
            id="meeting-type"
            value={meetingType}
            onChange={(e) => setMeetingType(e.target.value)}
            disabled={uploading || isProcessing}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
          >
            {MEETING_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        {/* File upload */}
        <div className="mb-6">
          <label htmlFor="audio-file" className="block text-sm font-medium text-gray-700 mb-2">
            Fichier audio/video/transcript
          </label>
          <div
            role="button"
            tabIndex={0}
            className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 transition-colors cursor-pointer"
            onClick={() => !uploading && !isProcessing && fileInputRef.current?.click()}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && !uploading && !isProcessing) {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
          >
            <input
              ref={fileInputRef}
              id="audio-file"
              type="file"
              accept={ACCEPTED_FORMATS}
              onChange={handleFileChange}
              disabled={uploading || isProcessing}
              className="sr-only"
            />
            {file ? (
              <div className="flex items-center justify-center gap-2 text-gray-700">
                <FileText size={20} className="text-blue-500" />
                <span className="font-medium">{file.name}</span>
                <span className="text-sm text-gray-500">
                  ({(file.size / (1024 * 1024)).toFixed(1)} Mo)
                </span>
              </div>
            ) : (
              <div>
                <Upload size={32} className="mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-500">
                  Cliquez ou deposez un fichier ici
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Formats : MP4, M4A, WAV, MP3, WebM, MKV, AVI, VTT, DOCX - Max {MAX_FILE_SIZE_MB} Mo
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Generate button */}
        {!jobId && (
          <button
            onClick={handleGenerate}
            disabled={!file || uploading}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {uploading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                {extractionProgress !== null
                  ? `Extraction audio... ${extractionProgress}%`
                  : 'Upload et generation en cours...'}
              </>
            ) : (
              <>
                <FileText size={18} />
                Generer le compte-rendu
              </>
            )}
          </button>
        )}

        {/* Status section */}
        {jobId && (
          <div className="mt-6 p-4 rounded-lg border bg-white" role="status" aria-live="polite">
            {jobId && !jobStatus && (
              <div className="flex items-center gap-3">
                <Loader2 size={20} className="animate-spin text-blue-500" />
                <span className="text-gray-700 font-medium">Initialisation...</span>
              </div>
            )}

            {isProcessing && (
              <div className="flex items-center gap-3">
                <Loader2 size={20} className="animate-spin text-blue-500" />
                <span className="text-gray-700 font-medium">
                  {jobStatus === 'transcribing' && sourceType === 'transcript'
                    ? STATUS_LABELS['processing']
                    : STATUS_LABELS[jobStatus] || 'Traitement en cours...'}
                </span>
              </div>
            )}

            {isCompleted && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle2 size={20} aria-hidden="true" />
                  <span className="font-medium">Compte-rendu genere avec succes</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <button
                    onClick={handleDownload}
                    disabled={downloading}
                    aria-label="Telecharger le compte-rendu en Markdown"
                    aria-busy={downloading}
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    {downloading ? (
                      <Loader2 size={18} className="animate-spin" aria-hidden="true" />
                    ) : (
                      <Download size={18} aria-hidden="true" />
                    )}
                    .md
                  </button>
                  <button
                    onClick={handleDownloadDocx}
                    disabled={downloadingDocx}
                    aria-label="Telecharger le compte-rendu en Word"
                    aria-busy={downloadingDocx}
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {downloadingDocx ? (
                      <Loader2 size={18} className="animate-spin" aria-hidden="true" />
                    ) : (
                      <Download size={18} aria-hidden="true" />
                    )}
                    .docx
                  </button>
                  <button
                    onClick={handleEmail}
                    disabled={sendingEmail}
                    aria-label="Envoyer le compte-rendu par email"
                    aria-busy={sendingEmail}
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                  >
                    {sendingEmail ? (
                      <Loader2 size={18} className="animate-spin" aria-hidden="true" />
                    ) : (
                      <Mail size={18} aria-hidden="true" />
                    )}
                    Email
                  </button>
                </div>
              </div>
            )}

            {isFailed && (
              <div className="flex items-center gap-2 text-red-600" role="alert">
                <AlertCircle size={20} aria-hidden="true" />
                <span className="font-medium">{error || 'La generation a echoue.'}</span>
              </div>
            )}

            {/* Reset button */}
            {(isCompleted || isFailed) && (
              <button
                onClick={handleReset}
                className="mt-4 w-full px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Nouveau compte-rendu
              </button>
            )}
          </div>
        )}
      </main>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default MeetingMinutes;
